import { Deployment } from '@liveauctioneers/hammer-ui-payments/types';

import { createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    getUserByToken,
    postFetchObfuscatedPhoneNumber,
    postLogin,
    postLoginWithTwoStepAuthentication,
    postSendTwoStepAuthentication,
} from '@/redux/modules/account/login/login.api';
import { activateTwoStepAuthentication } from '@/redux/modules/account/twoStepAuthentication/twoStepAuthentication.actions';
import { getSessionId } from '@/redux/modules/account/user/user.selectors';
import { getSignupCompletedAnalytics } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import { loadBidderData } from '@/redux/modules/bidder/data/bidderData.actions';
import { getDeployment } from '@/redux/modules/config';
import { setAuthCookie } from '@/redux/modules/cookies';
import { completeLoginModal } from '@/redux/modules/modal';
import getExpirationFromToken from '@/utils/getExpirationFromToken';

import {
    CodeDeliveryMethod,
    GetUserByTokenResponsePayload,
    LOGIN_FAIL,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
    LoginFailAction,
    LoginResponsePayload,
    LoginSuccessAction,
    PostLoginResponse,
    SubmitLoginInfo,
    SubmitTokenLoginInfo,
} from './login.types';

export const submitLogin = createAsyncThunk<LoginResponsePayload, SubmitLoginInfo>(
    'la/ui/login/submitLogin',
    async (
        {
            amplitudeExperimentFlags,
            emailAddress,
            featureFlags,
            location,
            newAccount,
            onLogin,
            password,
            shouldCloseModal = true,
            twoStepAuthenticationCode,
        },
        { dispatch, getState }
    ) => {
        dispatch({ type: LOGIN_REQUEST });
        try {
            const state = getState();
            const deployment = getDeployment(state);
            const sessionId = getSessionId();
            let response: PostLoginResponse;
            if (twoStepAuthenticationCode) {
                // if two-step authentication code is incorrect, an error is thrown.
                // submitLogin.rejected in login.reducer will handle it from there.
                response = await postLoginWithTwoStepAuthentication({
                    deployment,
                    password,
                    sessionId,
                    twoStepAuthenticationCode,
                    username: emailAddress,
                });
            } else {
                // if two-step authentication is needed, an error is thrown.
                // submitLogin.rejected in login.reducer will handle it from there.
                response = await postLogin({
                    deployment,
                    password,
                    sessionId,
                    username: emailAddress,
                });
            }

            const isProd = deployment === Deployment.Prod;
            // TODO: In order to release this code until SMS code verification is ready, we're going to avoid turning 2FA for new users
            //  Once SMS codes are being sent, we can (probably) remove the && false
            // if the user is a new user on prod, we must activate MFA
            // eslint-disable-next-line no-constant-condition -- gated off until SMS MFA is ready (see TODO above)
            if (newAccount && Boolean(response?.data?.bidder?.bidderId) && isProd && false) {
                dispatch(
                    activateTwoStepAuthentication({
                        bidderId: response.data.bidder.bidderId,
                        populatedNewAccountAuthToken: response.data.token,
                    })
                );
            }

            if (shouldCloseModal) {
                dispatch(completeLoginModal());
            }

            await Promise.all([
                dispatch(loadPageData({ amplitudeExperimentFlags, featureFlags, location })),
                dispatch(
                    loadBidderData({
                        bidderId: response.data.bidder.bidderId,
                        resellerType: response.data.resellerType,
                        token: response.data.token,
                    })
                ),
            ]);
            onLogin?.();

            const result: LoginResponsePayload = {
                ...response.data,
                bidder: {
                    ...response.data.bidder,
                    ANMS: response.data.annualNMS,
                },
            };
            // @ts-expect-error
            // We're spreading the API response, which still has annualNMS
            // To avoid unnecessary fields on the result, we're deleting it here
            delete result.annualNMS;

            // remove this once all dependencies are using toolkit and the thunk directly
            const loginSuccessAction: LoginSuccessAction = {
                error: false,
                meta: {
                    actionTime: Date.now(),
                },
                payload: result,
                type: LOGIN_SUCCESS,
            };
            dispatch(loginSuccessAction);

            const expires = getExpirationFromToken(response.data.token);
            dispatch(setAuthCookie(expires, response.data.token));

            return result;
        } catch (error) {
            const loginFailAction: LoginFailAction = {
                error: true,
                meta: { emailAddress },
                payload: error,
                type: LOGIN_FAIL,
            };

            dispatch(loginFailAction);

            throw error;
        }
    }
);

export const submitLoginWithToken = createAsyncThunk<LoginResponsePayload, SubmitTokenLoginInfo>(
    'la/ui/login/submitLoginWithToken',
    async (
        { amplitudeExperimentFlags, authToken, featureFlags, location, newAccount, onLogin, shouldCloseModal = true },
        { dispatch, getState }
    ) => {
        dispatch({ type: LOGIN_REQUEST });

        try {
            const state = getState();
            const deployment = getDeployment(state);

            const response: GetUserByTokenResponsePayload = await getUserByToken({
                authToken,
                deployment,
            });

            if (shouldCloseModal) {
                dispatch(completeLoginModal());
            }

            await Promise.all([
                dispatch(loadPageData({ amplitudeExperimentFlags, featureFlags, location })),
                dispatch(
                    loadBidderData({
                        bidderId: response.data.bidder.bidderId,
                        resellerType: response.data.resellerType,
                        token: authToken,
                    })
                ),
            ]);

            onLogin?.();

            if (newAccount) {
                getSignupCompletedAnalytics({
                    bidderId: response.data.bidder.bidderId,
                });
            }

            const result: LoginResponsePayload = {
                ...response.data,
                bidder: {
                    ...response.data.bidder,
                    ANMS: response.data.annualNMS,
                },
                token: authToken,
            };
            // @ts-expect-error
            // We're spreading the API response, which still has annualNMS
            // To avoid unnecessary fields on the result, we're deleting it here
            delete result.annualNMS;

            // remove this once all dependencies are using toolkit and the thunk directly
            const loginSuccessAction: LoginSuccessAction = {
                error: false,
                meta: {
                    actionTime: Date.now(),
                },
                payload: result,
                type: LOGIN_SUCCESS,
            };
            dispatch(loginSuccessAction);

            const expires = getExpirationFromToken(authToken);
            dispatch(setAuthCookie(expires, authToken));

            return result;
        } catch (error) {
            const loginFailAction: LoginFailAction = {
                error: true,
                meta: { emailAddress: '' },
                payload: error,
                type: LOGIN_FAIL,
            };

            dispatch(loginFailAction);

            throw error;
        }
    }
);

export const fetchObfuscatedPhoneNumber = createAsyncThunk<string, string>(
    'la/ui/login/fetchObfuscatedPhoneNumber',
    async (emailOrUsername, { getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const { payload } = await postFetchObfuscatedPhoneNumber({
            deployment,
            emailOrUsername,
        });
        return payload;
    }
);

export const sendTwoStepAuthenticationCodeToPhone = createAsyncThunk<void, string>(
    'la/ui/login/sendTwoStepAuthenticationCodeToPhone',
    async (emailOrUsername, { getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        await postSendTwoStepAuthentication({
            deliveryType: CodeDeliveryMethod.Sms,
            deployment,
            emailOrUsername,
        });
    }
);

export const sendTwoStepAuthenticationCodeToEmail = createAsyncThunk<void, string>(
    'la/ui/login/sendTwoStepAuthenticationCodeToEmail',
    async (emailOrUsername, { getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        await postSendTwoStepAuthentication({
            deliveryType: CodeDeliveryMethod.Email,
            deployment,
            emailOrUsername,
        });
    }
);
